import { ApolloError, OperationVariables, QueryOptions } from '@apollo/client';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import ICursorable from 'types/Cursorable';
import { getErrorMessage } from 'utils/errorUtils';
import { logError } from 'utils/testUtils';
import useApolloPaging from './useApolloPaging';

export default function useApolloPageAll<
  T extends ICursorable,
  // TODO: fix usage of {} as a type
  // eslint-disable-next-line @typescript-eslint/ban-types
  D extends { [key: string]: T[] } = {},
  V extends object = OperationVariables,
  O extends QueryOptions<V> = any
>(options: O, dataAccessorKey: string) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | ApolloError | null>();
  const [data, setData] = useState<T[]>();

  const { getAll } = useApolloPaging();

  const currentOptions = useRef<O>();

  useEffect(() => {
    if (!_.isEqual(currentOptions.current, options)) {
      currentOptions.current = options;
      setError('');
      setLoading(true);
      getAll<T, D, V>(dataAccessorKey, options)
        .then((res) => {
          setData(res);
          setLoading(false);
        })
        .catch((err: unknown) => {
          logError(err);
          setError(getErrorMessage(err));
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const get = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const res = await getAll<T, D, V>(dataAccessorKey, options);
      setData(res);
      setLoading(false);
    } catch (err: unknown) {
      logError(err);
      setError(getErrorMessage(err));
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAccessorKey, options]);

  return { data, error, loading, refetch: get };
}
